import React, { Fragment } from "react";
import { Menu, Transition } from "@headlessui/react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { BellIcon } from "@heroicons/react/24/outline";
import {
  clearNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from "../../redux/notificationSlice";
import NotificationAvatar from "./NotificationAvatar";
import { toastStatusForType, toastStatusToneClass } from "./notificationStatus";

const NotificationBellMenu = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const items = useSelector((state) => state.notifications.items);
  const unread = items.filter((n) => !n.read).length;
  const preview = items.slice(0, 12);

  return (
    <Menu as="div" className="relative">
      <Menu.Button type="button" className="rp-notify-bell-btn">
        <span className="sr-only">Notificaties</span>
        <BellIcon className="h-[1.15rem] w-[1.15rem]" aria-hidden="true" strokeWidth={2} />
        {unread > 0 ? (
          <span className="rp-notify-bell-badge">{unread > 99 ? "99+" : unread}</span>
        ) : null}
      </Menu.Button>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-150"
        enterFrom="transform opacity-0 scale-[0.97] translate-y-1"
        enterTo="transform opacity-100 scale-100 translate-y-0"
        leave="transition ease-in duration-100"
        leaveFrom="transform opacity-100 scale-100 translate-y-0"
        leaveTo="transform opacity-0 scale-[0.97] translate-y-1"
      >
        <Menu.Items className="rp-notify-panel absolute right-0 z-20 mt-2 origin-top-right focus:outline-none">
          <div className="rp-notify-panel__head">
            <div className="rp-notify-panel__title">
              <span>Meldingen</span>
              {items.length > 0 ? (
                <span className="rp-notify-panel__count">{items.length}</span>
              ) : null}
            </div>
            <div className="rp-notify-panel__actions">
              {items.length > 0 ? (
                <>
                  <button
                    type="button"
                    className="rp-notify-panel__link"
                    onClick={() => dispatch(markAllNotificationsRead())}
                  >
                    Alles gelezen
                  </button>
                  <button
                    type="button"
                    className="rp-notify-panel__link rp-notify-panel__link--muted"
                    onClick={() => dispatch(clearNotifications())}
                  >
                    Wissen
                  </button>
                </>
              ) : null}
            </div>
          </div>
          <div className="rp-notify-panel__scroll">
            {preview.length === 0 ? (
              <p className="rp-notify-panel__empty">Geen notificaties</p>
            ) : (
              preview.map((n) => {
                const { label: stLabel, tone } = toastStatusForType(n.type);
                return (
                  <Menu.Item key={n.id}>
                    {({ active }) => (
                      <button
                        type="button"
                        className={`rp-notify-row ${active ? "rp-notify-row--active" : ""} ${
                          !n.read ? "rp-notify-row--unread" : "rp-notify-row--read"
                        }`}
                        onClick={() => {
                          dispatch(markNotificationRead(n.id));
                          if (n.link) navigate(n.link);
                        }}
                      >
                        <div className="rp-notify-row__avatar-tile">
                          <NotificationAvatar
                            url={n.avatarUrl}
                            label={n.actorName}
                            className="h-8 w-8"
                          />
                        </div>
                        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                          {n.actorName ? (
                            <span className="text-[11px] font-semibold uppercase tracking-wide text-wa-muted">
                              {n.title === "Berichten" ? `Van ${n.actorName}` : n.actorName}
                            </span>
                          ) : null}
                          <span className="text-[14px] font-semibold leading-snug text-wa-text">
                            {n.title}
                          </span>
                          {n.message ? (
                            <span className="line-clamp-2 text-[12px] leading-relaxed text-wa-muted">
                              {n.message}
                            </span>
                          ) : null}
                          <span className="mt-1 flex items-center justify-between gap-2 border-t border-wa-border/40 pt-1.5">
                            <span className="text-[10px] font-semibold uppercase tracking-wide text-wa-muted">
                              Status
                            </span>
                            <span
                              className={`text-[11px] font-bold ${
                                n.read ? toastStatusToneClass("muted") : toastStatusToneClass(tone)
                              }`}
                            >
                              {n.read ? "Gelezen" : stLabel}
                            </span>
                          </span>
                          <span className="text-[10px] text-wa-muted/90">
                            {new Date(n.createdAt).toLocaleString("nl-BE", {
                              day: "2-digit",
                              month: "short",
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </span>
                        </span>
                      </button>
                    )}
                  </Menu.Item>
                );
              })
            )}
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  );
};

export default NotificationBellMenu;
